import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Typography,
    Alert,
    IconButton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Business as BusinessIcon, Add as AddIcon } from '@mui/icons-material';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../providers/toaster-provider';
import CompanySetup from './company-setup';

interface Company {
    entityId: string | number;
    entityName: string;
    role?: string;
}

interface ChooseCompanyProps {
    companies: Company[];
    onSelectCompany?: (company: Company) => void;
    loading?: boolean;
    onBack?: () => void;
    onCompanyCreated?: (company: Company) => void;
    fullWidth?: boolean;
}

const ChooseCompany: React.FC<ChooseCompanyProps> = ({
    companies,
    onSelectCompany,
    loading = false,
    onBack,
    onCompanyCreated,
    fullWidth = true,
}) => {
    const navigate = useNavigate();
    const { showError } = useToast();
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [isSelecting, setIsSelecting] = useState(false);
    const [showCreateStep, setShowCreateStep] = useState(false);
    const [companyList, setCompanyList] = useState<Company[]>(companies);

    // Sync with companies prop
    useEffect(() => {
        setCompanyList(companies);
    }, [companies]);

    // Refresh companies when coming back from create step
    useEffect(() => {
        if (!showCreateStep) {
            const entitiesCookie = Cookies.get('entities');
            if (entitiesCookie) {
                try {
                    const entities = JSON.parse(entitiesCookie);
                    setCompanyList(entities);
                    // Clear selection when refreshing list
                    setSelectedCompanyId('');
                } catch (e) {}
            }
        }
    }, [showCreateStep]);

    // Auto-select first company if only one exists
    useEffect(() => {
        if (companyList.length === 1 && !selectedCompanyId) {
            setSelectedCompanyId(String(companyList[0].entityId));
        }
    }, [companyList, selectedCompanyId]);

    const handleSelectCompany = async (companyToSelect?: Company) => {
        const selected = companyToSelect || companyList.find(c => String(c.entityId) === selectedCompanyId);
        
        if (!selected) {
            showError('Please select a company');
            return;
        }

        setIsSelecting(true);

        // Store selected company in cookie
        Cookies.set('selectedCompany', JSON.stringify(selected));

        if (onSelectCompany) {
            onSelectCompany(selected);
        } else {
            // Default behavior: navigate to transactions/dashboard
            navigate('/transactions');
        }

        setIsSelecting(false);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
            </Box>
        );
    }

    if (showCreateStep) {
        return (
            <CompanySetup
                onSuccess={(company) => {
                    if (onCompanyCreated) {
                        onCompanyCreated(company);
                    }
                    setShowCreateStep(false);
                }}
                onBack={() => setShowCreateStep(false)}
            />
        );
    }

    if (companies.length === 0) {
        return (
            <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                    You are not associated with any companies. Please contact your administrator.
                </Alert>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onBack}
                    startIcon={<ArrowBackIcon />}
                >
                    Back to Login
                </Button>
            </Box>
        );
    }

    return (
        <Box width={fullWidth ? '100%' : 'auto'}>
            {/* Header with back button */}
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={onBack} size="small" sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography fontSize={16} fontWeight={600}>
                    Select Company
                </Typography>
            </Box>

            <Typography variant="body2" color="textSecondary" mb={3}>
                You have access to {companyList.length} compan{companyList.length === 1 ? 'y' : 'ies'}. 
                Please select one to continue.
            </Typography>

            {/* Company Selection */}
            <Box
                sx={{
                    mb: 3,
                    maxHeight: 400,
                    overflowY: 'auto',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {companyList.map((company) => (
                        <Box key={company.entityId}>
                            <Card
                                sx={{
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        border: selectedCompanyId === String(company.entityId) ? '2px solid var(--color-primary)' : '1px solid #e0e0e0',
                                        backgroundColor: selectedCompanyId === String(company.entityId) ? 'rgba(0, 182, 155, 0.05)' : '#fff',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                            borderColor: 'var(--color-primary)',
                                        },
                                    }}
                                    onClick={() => handleSelectCompany(company)}
                                >
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <Box
                                                sx={{
                                                    backgroundColor: 'var(--color-primary)',
                                                    borderRadius: 1,
                                                    p: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <BusinessIcon sx={{ color: '#fff', fontSize: 22 }} />
                                            </Box>
                                            <Box flex={1} display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight={600}
                                                    fontSize={12}
                                                    sx={{
                                                        color: selectedCompanyId === String(company.entityId) ? 'var(--color-primary)' : 'inherit',
                                                        transition: 'color 0.3s ease',
                                                    }}
                                                >
                                                    {company.entityName}
                                                </Typography>
                                                {company.role && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'inline-block',
                                                            backgroundColor: 'var(--color-container-background)',
                                                            px: 1,
                                                            py: 0.5,
                                                            borderRadius: 0.5,
                                                            color: 'var(--color-text-muted)',
                                                        }}
                                                    >
                                                        {company.role === 'admin' ? 'Admin' : 'Read-Only'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        ))}
                    </Box>

                {/* Create Company Option */}
                <Card
                    onClick={() => setShowCreateStep(true)}
                    sx={{
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '2px dashed var(--color-primary)',
                        backgroundColor: 'rgba(0, 182, 155, 0.03)',
                        mt: 2,
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(0, 182, 155, 0.2)',
                            backgroundColor: 'rgba(0, 182, 155, 0.08)',
                        },
                    }}
                >
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <Box
                                sx={{
                                    backgroundColor: 'var(--color-primary)',
                                    borderRadius: '50%',
                                    p: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <AddIcon sx={{ color: '#fff', fontSize: 20 }} />
                            </Box>
                            <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{
                                    color: 'var(--color-primary)',
                                    transition: 'color 0.3s ease',
                                }}
                            >
                                Create New Company
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Action Buttons */}
            <Box display="flex" gap={2} justifyContent="space-between">
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onBack}
                    disabled={isSelecting}
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    );
};

export default ChooseCompany;
